import React, {FC, useState, useRef} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {Formik, FormikProps} from "formik";
import {message, Space, Steps, Modal, Button, Alert} from "antd";
import {ApolloQueryResult} from "@apollo/client";
import i18n from "../../../services/i18n";
import {
    Instructor,
    EditCourseLessonInput,
    GetCourseByIdQuery,
    EditCourseInput,
    DiscountUnit,
    GetInstructorsDocument,
    GetDetailedCourseTypesDocument,
    CourseNumberFormat,
    GetCourseLessonsByCourseIdDocument,
    GetBookingsByCourseDocument,
    GetCourseByIdDocument,
    MonthlySchedule,
    CoursePrice,
    Maybe,
    useUpdateCourseAndBookingsMutation,
    useUpdateCourseMutation,
    EditBookingInputBookableLessons,
    GetBookingsByCourseIdWithCourseLessonBookingsQuery,
    Course,
    GetBookingsByCourseIdWithCourseLessonBookingsQueryVariables,
    UpdateCourseAndBookingsInput,
    GetCourseDetailsByIdDocument,
    GetBookingsByCourseIdWithCourseLessonBookingsOptimizedDocument,
    CourseAdditionalItemInput,
    PaymentType,
} from "../../../generated/graphql";
import StepCourseData from "../StepCourseData";
import StepSettings from "../StepSettings";
import CourseCreateValidationSchema from "../CourseCreate/CourseCreateValidationSchema";
import {focusFirstAdditionalItemError, hasAdditionalItemErrors} from "../CourseViews/AdditionalItemsEditor";
import LayoutForm from "../../../views/LayoutForm/LayoutForm";
import {createInputHelpers} from "../../../helpers/createInputHelpers";
import Page from "../../../views/Page/Page";
import PageContentWrapper from "../../../views/PageContentWrapper/PageContentWrapper";
import FormButtonsStandard from "../../../views/FormButtonsStandard/FormButtonsStandard";
import {isDefined} from "../../../helpers/typeScriptHelpers";
import IncreaseMaxAttendeesModal from "../../../views/IncreaseMaxAttendeesModal/IncreaseMaxAttendeesModal";
import StepCoursePrice from "../StepCoursePrice";
import {panelWidth} from "../../../styles/layout";
import NextStepButton from "../CourseCreate/NextStepButton";
import {FormFieldNames, PossibleFormikPropsType} from "../CourseCreate/CourseCreateForm";
import {LocationState} from "../../../types";
import VisibilityAsTag from "../CourseViews/VisibilityAsTag";
import StepCourseLessonsLessonBookings from "../LessonBookings/StepCourseLessonsLessonBookings";
import LoadingContent from "../../../views/LoadingContent/LoadingContent";
import StepLoadingContent from "../StepLoadingContent";
import LessonBookingBadge from "../../common/LessonBookingBadge";
import StepCourseLessons from "../StepCourseLessons";
import ModalContainer from "../LessonBookings/ModalContainer";
import {useNewModelSave} from "../LessonBookings/NewModelSave";
import useHighestRole from "../../../helpers/useHighestRole";
import Roles from "../../../../shared/models/roles";
import ModifyBookingsModalTitle from "../LessonBookings/ModifyBookingsModalTitle";
import ModifyBookingsDocumentation from "../LessonBookings/ModifyBookingsDocumentation/ModifyBookingsDocumentation";
import {
    hasAnyBilledLessons,
    getBilledLessonIds,
    getBilledLessonCount,
    showBillingLockErrorModal,
} from "../helper/billingLockHelpers";
import {
    CourseLateEntryPricingFormFields,
    buildCourseLateEntryPricingPayload,
    getCourseLateEntryPricingInitialValues,
} from "../courseLateEntryPricing";

export type StepNames = "courseData" | "courseSettings" | "courseLessons" | "coursePrice";

const validationFields: {
    [K in StepNames]: Array<FormFieldNames>;
} = {
    courseData: ["courseTypeId", "locationId", "venueId", "customCourseNumberPrefix"],
    courseSettings: [
        "instructors",
        "minAttendees",
        "maxAttendees",
        "maxWaitlist",
        "annotation",
        "websiteInfo",
        "additionalShortInfo",
        "lateEntryPricingMode",
        "minimumPartialBookingLessonCount",
    ],
    courseLessons: ["cycle", "startDateTime", "endDateTime", "lessons"],
    coursePrice: ["paymentInterval", "fee", "vat", "discount", "discountUnit"],
};

type CourseEditFormProps = {
    course: GetCourseByIdQuery["courseById"];
    asModalContent?: boolean;
    fromPathUrl?: string;
    closeModal?: () => void;
    bookingsOfCourse?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"];
    refetchBookings?: (variables?: GetBookingsByCourseIdWithCourseLessonBookingsQueryVariables | undefined) => Promise<ApolloQueryResult<GetBookingsByCourseIdWithCourseLessonBookingsQuery>>;
    onAllowLessonBookingChange?: (allowLessonBooking: boolean) => void;
};

export type InstructorIdObject = {
    id: Instructor["id"];
};

const getNewestEntry = (coursePrices?: Array<Partial<CoursePrice>>): number => {
    if (!coursePrices) {
        return 0;
    }
    let newestEntry = coursePrices.length - 1;

    for (let i = coursePrices.length - 1; i >= 0; i--) {
        if (!coursePrices[i].isSpecial) {
            newestEntry = i;
            break;
        }
    }

    return newestEntry;
};

const sortLessonsByDate = (unsortedLessons: Array<EditCourseLessonInput>) => {
    return unsortedLessons.sort((a, b) => {
        return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime();
    });
};

const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
        event.preventDefault();
    }
};

const backToUrl = (fromPathUrl?: string, locationState?: LocationState) => {
    return fromPathUrl ?? locationState?.from?.pathname;
};

const stepStyle = (asModalContent?: boolean): React.CSSProperties => ({
    maxHeight: `calc(80vh - ${60 * 2}px)`,
    overflowY: "auto",
});

const lessonsHaveChanged = (initialLessons: Array<EditCourseLessonInput>, currentLessons: Array<EditCourseLessonInput>): boolean => {
    if (initialLessons.length !== currentLessons.length) {
        return true;
    }

    return initialLessons.some((initialLesson, index) => {
        const currentLesson = currentLessons[index];

        return (
            initialLesson.id !== currentLesson.id ||
            initialLesson.startDateTime !== currentLesson.startDateTime ||
            initialLesson.endDateTime !== currentLesson.endDateTime ||
            initialLesson.locationId !== currentLesson.locationId ||
            initialLesson.venueId !== currentLesson.venueId
        );
    });
};

// Erkennt, ob sich Kurs-Pricing-Felder geändert haben, die der Server zur Neuberechnung
// der ScheduledPayments für bestehende Buchungen verwendet. Wird zusammen mit
// `lessonsHaveChanged` als Trigger für das "Buchungen anpassen"-Modal genutzt.
//
// Hintergrund: Der Server (UpdateCourseAndBookings) regeneriert ScheduledPayments aus
// grossPrice / vat / installmentRates / paymentType / paymentInterval / monthlySchedule
// — nicht aus den im updatedBookingsData mitgeschickten Beträgen. Ein Diff auf den
// scheduledPayments im Form-State ist daher nicht aussagekräftig.
const coursePricingHasChanged = (
    initial: Pick<EditCourseInput, "fee" | "vat" | "discount" | "discountUnit" | "paymentInterval" | "paymentType" | "monthlySchedule" | "dateOfFirstInstallment" | "installmentRates" | "allowMonthlyPayment" | "allowUpFrontPayment">,
    current: Pick<EditCourseInput, "fee" | "vat" | "discount" | "discountUnit" | "paymentInterval" | "paymentType" | "monthlySchedule" | "dateOfFirstInstallment" | "installmentRates" | "allowMonthlyPayment" | "allowUpFrontPayment">,
): boolean => {
    if ((initial.fee ?? null) !== (current.fee ?? null)) return true;
    if ((initial.vat ?? null) !== (current.vat ?? null)) return true;
    if ((initial.discount ?? null) !== (current.discount ?? null)) return true;
    if (initial.discountUnit !== current.discountUnit) return true;
    if (initial.paymentInterval !== current.paymentInterval) return true;
    if (initial.paymentType !== current.paymentType) return true;
    if (initial.monthlySchedule !== current.monthlySchedule) return true;
    if (initial.dateOfFirstInstallment !== current.dateOfFirstInstallment) return true;
    if (initial.allowMonthlyPayment !== current.allowMonthlyPayment) return true;
    if (initial.allowUpFrontPayment !== current.allowUpFrontPayment) return true;

    const initRates = initial.installmentRates ?? [];
    const currRates = current.installmentRates ?? [];
    if (initRates.length !== currRates.length) return true;

    return initRates.some((r, i) => {
        const c = currRates[i];
        if (!c) return true;
        return r.installmentAmount !== c.installmentAmount || r.installmentDue !== c.installmentDue;
    });
};

// Erkennt, ob für mindestens eine bestehende Buchung der pro-Buchung-Rabatt oder die
// Zahlungsart manuell geändert wurde (relativ zum Server-Stand). Greift z.B. wenn der User
// im LessonBookings-Step direkt einen Rabatt auf einer einzelnen Buchung anpasst.
const bookingDiscountsHaveChanged = (
    bookings: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"] | undefined,
    updatedBookingsData: UpdateCourseAndBookingsInput["updatedBookingsData"] | undefined,
): boolean => {
    if (!updatedBookingsData?.length || !bookings?.length) {
        return false;
    }

    return updatedBookingsData.some((updated) => {
        const original = bookings.find((b) => b.id === updated.id);
        if (!original) return false;

        if ((original.discount ?? 0) !== (updated.discount ?? 0)) return true;
        if ((original.memberDiscount ?? 0) !== (updated.memberDiscount ?? 0)) return true;
        if (original.paymentType !== updated.paymentType) return true;

        return false;
    });
};

const renderSubtitle = ({text, course}: {text?: Maybe<string>; course: GetCourseByIdQuery["courseById"]}) => {
    if (course.visibility) {
        return (
            <Space size={15}>
                <div>{text}</div>
                <div
                    style={{
                        paddingBottom: 4,
                        marginTop: -4,
                    }}
                >
                    <VisibilityAsTag
                        visibility={course.visibility}
                        blockBookingOnline={course.blockBookingOnline}
                        sizeMultiplier={0.75}
                        style={{
                            opacity: 0.75,
                        }}
                    />
                </div>
            </Space>
        );
    }

    return text;
};

const CourseEditForm: FC<CourseEditFormProps> = ({course, asModalContent, fromPathUrl, closeModal, bookingsOfCourse, refetchBookings, onAllowLessonBookingChange}) => {
    // Billing-Status aus den originalen Query-Daten berechnen (nicht aus Formik-Values)
    const billedLessonsData = hasAnyBilledLessons(course?.lessons ?? []);
    const billedLessonIdsList = getBilledLessonIds(course?.lessons ?? []);
    const billedCount = getBilledLessonCount(course?.lessons ?? []);

    const [step, setStep] = useState(0);
    const [showIncreaseMaxAttendeesModal, setShowIncreaseMaxAttendeesModal] = useState(false);
    const [showModifyBookingsModal, setShowModifyBookingsModal] = useState(false);
    const [showModifyBookingsDocumentation, setShowModifyBookingsDocumentation] = useState(false);
    // Modus „Neu“ im Speicher-Dialog (nur Superuser, ADR 0020 Stufe 2): speichert über den neuen Preispfad.
    const [plannerMode, setPlannerMode] = useState(false);
    const isSuperUser = useHighestRole() === Roles.Superuser;
    // Buchungsdaten beim Öffnen des Speicher-Dialogs als Kopie (einige Editoren ändern die Objekte direkt):
    // Änderungen im Dialog übernimmt der neue Pfad noch nicht (Stufe 4), sie dürfen nicht still verloren gehen.
    const bookingsDataAtModalOpen = useRef<string | null>(null);
    const [editCourseValues, setEditCourseValues] = useState<EditCourseInput>(course);
    const [pendingFormikProps, setPendingFormikProps] = useState<any>(null);
    const [pendingValues, setPendingValues] = useState<any>(null);
    const formikPropsRef = useRef<FormikProps<PossibleFormikPropsType> | null>(null);
    const history = useHistory();
    const location = useLocation<LocationState>();

    const refetchQueries = [
        "GetInstructors",
        {query: GetInstructorsDocument},
        "GetDetailedCourseTypes",
        {query: GetDetailedCourseTypesDocument},
        "GetCourseLessonsByCourseId",
        {
            query: GetCourseLessonsByCourseIdDocument,
            variables: {courseId: course.id},
        },
        "GetBookingsByCourse",
        {query: GetBookingsByCourseDocument, variables: {id: course.id}},
        "GetCourseById",
        {query: GetCourseByIdDocument, variables: {id: course.id}},
        "GetCourseDetailsById",
        {query: GetCourseDetailsByIdDocument, variables: {id: course.id}},
        "GetBookingsByCourseIdWithCourseLessonBookingsOptimized",
        {
            query: GetBookingsByCourseIdWithCourseLessonBookingsOptimizedDocument,
            variables: {courseId: course.id},
        },
    ];

    const [updateCourseAndBookings, {loading: mutationLoading, error: mutationError}] = useUpdateCourseAndBookingsMutation({
        refetchQueries,
    });
    const saveWithNewModel = useNewModelSave(refetchQueries);
    const [updateRegularCourse, {loading: regularMutationLoading, error: regularMutationError}] = useUpdateCourseMutation({
        refetchQueries,
    });

    const graphqlLoading = mutationLoading || regularMutationLoading;
    const graphqlError = mutationError || regularMutationError;
    const stepUp = () => setStep(step + 1);
    const {prefixedCourseNumber, ...initialCourseValues} = course;
    const latestPriceData = getNewestEntry(initialCourseValues.coursePrice);
    const initialValues = prepareInitialValues(initialCourseValues, latestPriceData, bookingsOfCourse);

    const handleStepChange = (
        currentStep: number,
        formikProps: FormikProps<PossibleFormikPropsType>,
        validationFields: {
            [K in StepNames]: Array<FormFieldNames>;
        },
    ) => {
        formikProps.validateForm().then((errors: {}) => {
            if (currentStep > step) {
                const stepNames = Object.keys(validationFields) as Array<StepNames>;
                const currentStepName = stepNames[step];
                const currentFields = validationFields[currentStepName];

                // FieldArray-Felder (z.B. "lessons") dürfen nicht mit setFieldTouched(field, true) markiert
                // werden, da Formik den touched-State von Array zu Boolean korrumpiert. Danach crasht
                // FieldArray.remove() mit "array.slice is not a function" auf dem Boolean-Wert.
                const fieldArrayFields: ReadonlyArray<string> = ["lessons"];

                currentFields.forEach((field) => {
                    if (!fieldArrayFields.includes(field)) {
                        formikProps.setFieldTouched(field, true);
                    }
                });

                const errorsIncludesOneOfTheseFields = () => {
                    const validationErrors = Object.keys(errors);

                    return currentFields.some((field) => validationErrors.includes(field));
                };

                if (errorsIncludesOneOfTheseFields()) {
                    setStep(step);
                } else {
                    setStep(currentStep);
                }
            } else {
                setStep(currentStep);
            }
        });
    };

    const processFormData = (values: EditCourseInput) => {
        const instructorsIds = Array.isArray(values.instructors) ? values.instructors.map(({id}) => ({id})) : [];

        const courseLessonInput = Array.isArray(values.lessons)
            ? values.lessons.map((lesson: EditCourseLessonInput) => ({
                  id: lesson.id,
                  courseId: course.id,
                  locationId: lesson.locationId,
                  venueId: lesson.venueId,
                  instructors: Array.isArray(lesson.instructors) ? lesson.instructors.map(({id}) => ({id})) : undefined,
                  startDateTime: lesson.startDateTime,
                  endDateTime: lesson.endDateTime,
              }))
            : [];

        const includedEntranceFee = initialCourseValues.coursePrice[latestPriceData]?.includedEntranceFee ?? null;
        const installmentRates = values.installmentRates;
        const installmentRatesWithCourseId = installmentRates?.map((rate) => ({
            ...rate,
            courseId: course.id,
        }));

        const discountUnit = values.discountUnit !== undefined && values.discountUnit !== null ? values.discountUnit : DiscountUnit.Euro;

        return {
            editCourseData: {
                // ...values,
                courseTypeId: values.courseTypeId,
                courseNumberFormat: values.courseNumberFormat,
                customCourseNumberPrefix: values.customCourseNumberPrefix,
                locationId: values.locationId,
                venueId: values.venueId,
                coursePrices: [
                    {
                        courseId: course.id,
                        grossPrice: values.fee,
                        vat: values.vat,
                        discount: values.discount,
                        discountUnit: discountUnit as DiscountUnit,
                        includedEntranceFee: includedEntranceFee ?? null,
                    },
                ],
                paymentInterval: values.paymentInterval,
                cycle: values.cycle,
                startDateTime: sortLessonsByDate(courseLessonInput)[0].startDateTime,
                endDateTime: sortLessonsByDate(courseLessonInput)[0].endDateTime,
                annotation: values.annotation,
                websiteInfo: values.websiteInfo,
                additionalShortInfo: values.additionalShortInfo ?? undefined,
                minAttendees: values.minAttendees,
                maxAttendees: values.maxAttendees,
                maxAssistantsPerLesson:
                    values.maxAssistantsPerLesson || values.maxAssistantsPerLesson === 0
                        ? Number(values.maxAssistantsPerLesson)
                        : null,
                maxWaitlist: values.maxWaitlist || values.maxWaitlist === 0 ? Number(values.maxWaitlist) : null,
                instructors: instructorsIds,
                lessons: courseLessonInput,
                allowMonthlyPayment: values.allowMonthlyPayment,
                allowUpFrontPayment: values.allowUpFrontPayment,
                allowMolliePayment: values.allowMolliePayment,
                // Phase 3: optionalen Modus persistieren, damit das 3-Zustands-Panel
                // beim Bearbeiten korrekt gespeichert wird.
                mollieOptional: values.mollieOptional,
                monthlySchedule: values.monthlySchedule,
                isActive: values.isActive,
                showInWeb: values.showInWeb,
                installmentRates: installmentRatesWithCourseId,
                paymentType: values.paymentType,
                dateOfFirstInstallment: values.paymentType === PaymentType.Monthly ? installmentRatesWithCourseId?.[0]?.installmentDue ?? values.dateOfFirstInstallment : values.dateOfFirstInstallment,
                discount: values.discount ?? null,
                discountUnit: values.discountUnit,
                allowLessonBooking: values.allowLessonBooking,
                customInvoiceDueDays: values.customInvoiceDueDays ?? null,
                // Zusatzposten: undefined = unverändert lassen; auf Einzelbuchungs-
                // Kursen alle löschen (Server verbietet Posten dort). ZP-16:
                // Zeilen werden NICHT still gefiltert — ungültige Zeilen zeigt
                // der Editor inline an, der Server-Guard lehnt sie verständlich ab.
                additionalItems: values.allowLessonBooking
                    ? []
                    : values.additionalItems,
                // §4.2 Optimistic Concurrency: alle beim Laden sichtbaren IDs —
                // parallel angelegte Zeilen führen serverseitig zum Konflikt
                // statt zum stillen Löschen.
                additionalItemsKnownIds: (initialValues.additionalItems ?? [])
                    .map((item) => item.id)
                    .filter((id): id is string => Boolean(id)),
                ...buildCourseLateEntryPricingPayload(
                    values as CourseLateEntryPricingFormFields,
                ),
            },
            maxAttendeeAmountHasBeenIncreased: values?.maxAttendees && initialValues.maxAttendees != null && values.maxAttendees > initialValues.maxAttendees,
            courseLessonInput,
        };
    };

    const handleBookingCheck = async (
        values: UpdateCourseAndBookingsInput,
        formikProps: FormikProps<PossibleFormikPropsType>,
        resetForm: () => void,
        setSubmitting: (isSubmitting: boolean) => void,
    ) => {
        // §4.3: Ungültige Zusatzposten blockieren den Submit — der Fehler wird
        // inline gezeigt statt erst vom Server abgelehnt.
        if (!values.allowLessonBooking && hasAdditionalItemErrors((values.additionalItems as Array<CourseAdditionalItemInput> | undefined) ?? undefined)) {
            focusFirstAdditionalItemError((values.additionalItems as Array<CourseAdditionalItemInput> | undefined) ?? undefined);
            message.error(i18n.containers.courses.CourseCreate.additionalItems.blockingValidationError());
            setSubmitting(false);

            return;
        }

        const {editCourseData, maxAttendeeAmountHasBeenIncreased} = processFormData(values);

        setEditCourseValues(editCourseData);

        try {
            const courseId = course.id;

            if (!courseId) {
                message.error("222 Die courseId fehlt!!!");
                throw new Error("222 Course ID is required");
            }

            const updatedBookingsData = values.updatedBookingsData;

            // Prüfe ob sich Lektionen geändert haben
            const lessonsChanged = lessonsHaveChanged(initialValues.lessons, values.lessons);

            await refetchBookings?.({
                courseId: course.id,
            }).then(async (res) => {
                const waitingListCount = res.data.bookingsByCourseIdWithCourseLessonBookings.filter((booking) => booking.bookingNumberPrefix === "W").length;
                const bookingCount = res.data.bookingsByCourseIdWithCourseLessonBookings.filter((booking) => booking.bookingNumberPrefix !== "W" && booking.canceled === null).length;

                let bookableSlots = 0;

                if (values.maxAttendees) {
                    bookableSlots = values.maxAttendees - bookingCount;
                }

                // Auch Preis-/Raten-/Rabatt-Änderungen sollen das Modal öffnen, damit der User
                // die geänderten Buchungen sieht, bevor sie gespeichert werden.
                const pricingChanged = coursePricingHasChanged(initialValues, values);
                const bookingDiscountsChanged = bookingDiscountsHaveChanged(
                    res.data.bookingsByCourseIdWithCourseLessonBookings,
                    updatedBookingsData,
                );

                // Wenn Lektionen, Kurspreis oder Buchungs-Rabatte geändert wurden und es Buchungen gibt, zeige das ModifyBookingsModal
                if ((lessonsChanged || pricingChanged || bookingDiscountsChanged) && bookingCount > 0) {
                    setPendingFormikProps({formikProps});
                    setPendingValues({editCourseData, updatedBookingsData, resetForm, setSubmitting});
                    bookingsDataAtModalOpen.current = JSON.stringify(updatedBookingsData ?? null);
                    setShowModifyBookingsModal(true);
                } else if (maxAttendeeAmountHasBeenIncreased && bookableSlots > 0 && waitingListCount > 0) {
                    setShowIncreaseMaxAttendeesModal(true);
                } else {
                    await handleUpdateCourse(editCourseData, updatedBookingsData, resetForm, setSubmitting);
                }
            });
        } catch (error) {
            console.log("error refetching bookings", error);
            message.error("Kurs konnte nicht aktualisiert werden");
        } finally {
            setSubmitting(false);
        }
    };

    const handleModifyBookingsModalClose = () => {
        setShowModifyBookingsModal(false);
        setPendingFormikProps(null);
        setPendingValues(null);
        // Jedes Öffnen startet im Modus „Heute“, wie vorher mit dem Zustand im ModalContainer.
        setPlannerMode(false);
    };

    const handleModifyBookingsModalConfirm = async () => {
        if (pendingValues) {
            const {editCourseData, updatedBookingsData: snapshotUpdatedBookingsData, resetForm, setSubmitting} = pendingValues;
            // Live-Werte aus dem Formik-State lesen, damit User-Edits im Modal
            // (z. B. Ankreuzen einer neu hinzugefügten Lesson für eine Buchung)
            // mitgespeichert werden. Fallback auf den Snapshot, falls die Ref leer ist.
            // Cast nötig: PossibleFormikPropsType ist NewCourseInput | UpdateCourseAndBookingsInput,
            // updatedBookingsData existiert nur auf der zweiten Variante.
            const liveValues = formikPropsRef.current?.values as UpdateCourseAndBookingsInput | undefined;
            const updatedBookingsData = liveValues?.updatedBookingsData ?? snapshotUpdatedBookingsData;
            const newModel = isSuperUser && plannerMode;

            const closeModifyBookingsModal = () => {
                setShowModifyBookingsModal(false);
                setPendingFormikProps(null);
                setPendingValues(null);
                setPlannerMode(false);
            };

            if (newModel) {
                // Buchungsänderungen im Dialog oder im Formular (Rabatt, Zahlweise) übernimmt der neue Pfad noch nicht (Stufe 4).
                const editedInModal = bookingsDataAtModalOpen.current !== null && JSON.stringify(updatedBookingsData ?? null) !== bookingsDataAtModalOpen.current;

                if (editedInModal || bookingDiscountsHaveChanged(bookingsOfCourse, updatedBookingsData)) {
                    Modal.error({
                        title: "Nicht im neuen Preismodell möglich",
                        content:
                            "Es wurden Buchungen geändert (Einheiten, Zahlweise, Raten oder Rabatt). Diese Änderungen übernimmt das neue Preismodell noch nicht. Bitte im Modus „Heute“ speichern oder die Änderungen verwerfen.",
                        okText: "Verstanden",
                        width: 560,
                    });

                    return;
                }
                // Der Dialog bleibt bei Abbruch, Sperrgrund oder Fehler offen; geschlossen wird erst nach dem Speichern.
                await handleUpdateCourse(editCourseData, updatedBookingsData, resetForm, setSubmitting, closeModifyBookingsModal);

                return;
            }
            closeModifyBookingsModal();
            await handleUpdateCourse(editCourseData, updatedBookingsData, resetForm, setSubmitting);
        }
    };

    const handleUpdateCourse = async (
        editCourseData: EditCourseInput,
        updatedBookingsData: UpdateCourseAndBookingsInput["updatedBookingsData"] | undefined,
        resetForm: () => void,
        setSubmitting: (isSubmitting: boolean) => void,
        // Gesetzt = Speichern im neuen Preismodell; wird nach erfolgreichem Speichern vor der Navigation aufgerufen.
        onNewModelSaved?: () => void,
    ) => {
        const courseId = course.id;

        if (!courseId) {
            message.error("Die courseId fehlt!!!");
            throw new Error("Course ID is required");
        }
        // // RENDER --------------------------------------------------------------------------------
        const prefixLessonIds = (lessons: Array<EditCourseLessonInput>) => {
            return lessons.map((lesson) => {
                if (lesson.id) {
                    return lesson;
                }

                return {
                    ...lesson,
                    id: `new-${lesson.id}`,
                };
            });
        };

        const editCourseDataWithPrefixedLessonIds = {
            ...editCourseData,
            lessons: prefixLessonIds(editCourseData.lessons),
        };

        const allowLessonBooking = editCourseData.allowLessonBooking;

        if (allowLessonBooking) {
            try {
                if (onNewModelSaved) {
                    // Zusammenfassung, Bestätigung und Fehler zeigt der Hook selbst; bei Abbruch bleibt der Dialog offen.
                    const saved = await saveWithNewModel({courseId: course.id, ...editCourseData, updatedBookingsData});

                    if (!saved) return;
                    onNewModelSaved();
                } else {
                    await updateCourseAndBookings({
                        variables: {
                            dryRun: false,
                            updateCourseAndBookingsInput: {
                                courseId: course.id,
                                ...editCourseData,
                                // ...editCourseDataWithPrefixedLessonIds,
                                updatedBookingsData,
                            },
                        },
                        fetchPolicy: "no-cache",
                    });
                }
                setStep(0);
                resetForm();
                const backTo = backToUrl(fromPathUrl, location.state);

                if (backTo) {
                    history.push(backTo);
                } else {
                    history.goBack();
                }
                // backToUrl(fromPathUrl, location.state)
                //     ? history.push(backToUrl(fromPathUrl, location.state))
                //     : history.goBack();
                closeModal?.();
                message.success("Kurs wurde aktualisiert");
            } catch (error) {
                console.debug("courseId", courseId);
                console.debug("course", course);
                console.debug("editCourseData", editCourseData);
                console.debug("updatedBookingsData", updatedBookingsData);
                console.log("error updating course", error);

                // Extrahiere Fehlermeldung aus GraphQL-Fehler
                const errorMessage = (error as any)?.graphQLErrors?.[0]?.message || (error as any)?.message || "Unbekannter Fehler";

                // Spezifische Fehlerbehandlung mit Modal für kritische Fehler
                if (errorMessage.includes("transaction is aborted") || errorMessage.includes("serialization conflict") || errorMessage.includes("duplicate key")) {
                    Modal.error({
                        title: "⚠️ Kurs-Update fehlgeschlagen",
                        content: (
                            <div>
                                <p>
                                    <strong>Fehler:</strong> {errorMessage}
                                </p>
                                <p style={{marginTop: "1rem", fontSize: "0.9rem", color: "#666"}}>{"Das Update konnte nicht abgeschlossen werden. Dies kann auftreten, wenn:"}</p>
                                <ul style={{marginTop: "0.5rem", fontSize: "0.9rem"}}>
                                    <li>{"Mehrere Updates gleichzeitig durchgeführt werden"}</li>
                                    <li>{"Die Datenbankverbindung unterbrochen wurde"}</li>
                                    <li>{"Neue Lektionen mit den bestehenden Buchungen in Konflikt geraten"}</li>
                                </ul>
                                <p style={{marginTop: "1rem", fontSize: "0.9rem", color: "#666"}}>{"Bitte versuche es in wenigen Sekunden erneut."}</p>
                            </div>
                        ),
                        okText: "Verstanden",
                        width: 500,
                    });
                } else if (errorMessage.includes("maximale Anzahl") || errorMessage.includes("Keine Kursstunden") || errorMessage.includes("Buchungen in Konflikt")) {
                    // Business-Logic Fehler zeigen auch ein Modal
                    const errorMessageCleaned = errorMessage.replace("Bad Request: updateCourseAndBookings: Error:", "");

                    Modal.error({
                        title: "Überschreitung der maximalen Anzahl von Kurseinheiten",
                        content: (
                            <div>
                                <p>{errorMessageCleaned}</p>
                                <p style={{marginTop: "1rem", fontSize: "0.9rem", color: "#666"}}>{"Bitte überprüfe deine Eingaben und versuche es erneut."}</p>
                            </div>
                        ),
                        okText: "Verstanden",
                        width: 500,
                    });
                } else if (errorMessage.includes("abgerechnete Kurseinheiten") || errorMessage.includes("bereits abgerechnet")) {
                    showBillingLockErrorModal(errorMessage);
                } else {
                    // Gewollte Server-Meldungen (Guards wie #734 H3) nennen Buchung und Abhilfe; technische Fehler bleiben generisch.
                    const guardMessage = /^Bad Request: updateCourseAndBookings: (?:Bad Request|ForbiddenError|UserInputError): ([\s\S]+)$/.exec(errorMessage)?.[1];

                    if (guardMessage) {
                        Modal.error({title: "Kurs konnte nicht aktualisiert werden", content: guardMessage, okText: "Verstanden", width: 500});
                    } else {
                        message.error("Kurs konnte nicht aktualisiert werden");
                    }
                }
            } finally {
                // message.success("Kurs wurde aktualisiert");
                setSubmitting(false);
            }
        } else {
            try {
                await updateRegularCourse({
                    variables: {
                        id: course.id,
                        editCourseData,
                    },
                });
                setStep(0);
                resetForm();
                const backTo = backToUrl(fromPathUrl, location.state);

                if (backTo) {
                    history.push(backTo);
                } else {
                    history.goBack();
                }
                closeModal?.();
                message.success("Kurs wurde aktualisiert");
            } catch (error) {
                console.debug("courseId", courseId);
                console.debug("course", course);
                console.debug("editCourseData", editCourseData);
                console.debug("updatedBookingsData", updatedBookingsData);
                console.log("error updating course", error);

                const errorMessage = (error as any)?.graphQLErrors?.[0]?.message || (error as any)?.message || "Unbekannter Fehler";

                if (errorMessage.includes("abgerechnete Kurseinheiten") || errorMessage.includes("bereits abgerechnet")) {
                    showBillingLockErrorModal(errorMessage);
                } else {
                    message.error("Kurs konnte nicht aktualisiert werden");
                }
            } finally {
                setSubmitting(false);
            }
        }
    };

    const renderSteps = (formikProps: FormikProps<EditCourseInput>) => {
        const {validateStatus} = createInputHelpers<EditCourseInput>(formikProps);
        // FormFieldNames umfasst auch Felder der anderen Kurs-Inputs; validateStatus prüft nur bekannte Keys.
        const stepStatus = (fields: Array<FormFieldNames>) => validateStatus(fields as Array<keyof EditCourseInput & string>);
        const customCourseNumberPrefix = formikProps.values.customCourseNumberPrefix;
        const courseNumberFormat = formikProps.values.courseNumberFormat;
        const customeCourseNumberPrefixEmpty = courseNumberFormat === CourseNumberFormat.CustomPrefix && customCourseNumberPrefix === undefined ? "error" : undefined;
        const statusCourseData = stepStatus(validationFields.courseData) ?? customeCourseNumberPrefixEmpty;

        return (
            <Steps
                current={step}
                onChange={(currentStep) => handleStepChange(currentStep, formikProps, validationFields)}
                style={{
                    maxWidth: panelWidth,
                    marginLeft: "auto",
                    marginRight: "auto",
                }}
                items={[
                    {title: "Kursdaten", status: statusCourseData},
                    {title: "Einstellungen", status: stepStatus(validationFields.courseSettings)},
                    {title: "Kurstermine", status: stepStatus(validationFields.courseSettings)},
                    {title: "Kurspreis", status: stepStatus(validationFields.coursePrice)},
                ]}
            />
        );
    };

    const renderStepContent = (formikProps: FormikProps<EditCourseInput>, status: any) => {
        const error = status !== undefined && status.graphqlError === undefined ? mutationError : undefined;

        if (graphqlLoading) {
            return (
                <StepLoadingContent
                    loadingText="Kursdaten werden gespeichert"
                    title="Speichern"
                    subtitle={renderSubtitle({
                        text: prefixedCourseNumber,
                        course,
                    })}
                    error={error}
                />
            );
        }

        switch (step) {
            case 0:
                return (
                    <StepCourseData
                        key="CourseData"
                        formikProps={formikProps}
                        onNextStep={stepUp}
                        error={error}
                        nextStepButton={<NextStepButton formikProps={formikProps} fields={validationFields.courseData} onNextStep={stepUp} />}
                        subtitle={renderSubtitle({
                            text: prefixedCourseNumber,
                            course,
                        })}
                        hasBilledLessons={billedLessonsData}
                    />
                );
            case 1:
                return (
                    <StepSettings
                        key="Settings"
                        formikProps={formikProps}
                        error={error}
                        nextStepButton={<NextStepButton formikProps={formikProps} fields={validationFields.courseSettings} onNextStep={stepUp} />}
                        subtitle={renderSubtitle({
                            text: prefixedCourseNumber,
                            course,
                        })}
                        courseNumber={prefixedCourseNumber ?? undefined}
                        hasBilledLessons={billedLessonsData}
                    />
                );
            case 2:
                return (
                    <>
                        {formikProps.values.allowLessonBooking ? (
                            <StepCourseLessonsLessonBookings
                                key="CourseLessons"
                                formikProps={formikProps}
                                error={error}
                                nextStepButton={<NextStepButton formikProps={formikProps} fields={validationFields.courseLessons} onNextStep={stepUp} />}
                                courseNumber={prefixedCourseNumber ?? undefined}
                                subtitle={renderSubtitle({
                                    text: prefixedCourseNumber,
                                    course,
                                })}
                                style={stepStyle(asModalContent)}
                                bookingsOfCourse={bookingsOfCourse}
                                hasBilledLessons={billedLessonsData}
                                billedLessonIds={billedLessonIdsList}
                            />
                        ) : (
                            <StepCourseLessons
                                key="CourseLessons"
                                formikProps={formikProps}
                                error={error}
                                nextStepButton={<NextStepButton formikProps={formikProps} fields={validationFields.courseLessons} onNextStep={stepUp} />}
                                courseNumber={prefixedCourseNumber ?? undefined}
                                subtitle={renderSubtitle({
                                    text: prefixedCourseNumber,
                                    course,
                                })}
                                style={stepStyle(asModalContent)}
                                hasBilledLessons={billedLessonsData}
                                billedLessonIds={billedLessonIdsList}
                            />
                        )}
                    </>
                );

            case 3:
                return (
                    <StepCoursePrice
                        key="CoursePrice"
                        onSave={formikProps.handleSubmit}
                        formikProps={formikProps}
                        error={error}
                        subtitle={renderSubtitle({
                            text: prefixedCourseNumber,
                            course,
                        })}
                        style={stepStyle(asModalContent)}
                    />
                );
            default:
                return null;
        }
    };

    const renderContent = () => {
        return (
            <PageContentWrapper>
                <IncreaseMaxAttendeesModal
                    open={showIncreaseMaxAttendeesModal}
                    setModalVisible={(e: boolean) => setShowIncreaseMaxAttendeesModal(e)}
                    courseId={course.id}
                    editCourseData={editCourseValues}
                    fromPathUrl={fromPathUrl}
                />
                <Formik<EditCourseInput>
                    key={`course-edit-form-${course.id}`}
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={CourseCreateValidationSchema}
                    onSubmit={async (values, {resetForm, setSubmitting}) => {
                        if (formikPropsRef.current) {
                            const valuesWithCourseId: UpdateCourseAndBookingsInput = {
                                ...values,
                                courseId: course.id,
                            };

                            await handleBookingCheck(valuesWithCourseId, formikPropsRef.current, resetForm, setSubmitting);
                        }
                    }}
                >
                    {(formikProps) => {
                        formikPropsRef.current = formikProps as FormikProps<PossibleFormikPropsType>;
                        const {handleSubmit, status, resetForm} = formikProps;

                        return (
                            <>
                                <FormikObserver formikProps={formikProps} onAllowLessonBookingChange={onAllowLessonBookingChange} />
                                {showModifyBookingsModal && (
                                    <Modal
                                        title={
                                            <ModifyBookingsModalTitle
                                                courseNumber={prefixedCourseNumber ?? undefined}
                                                onInfoClick={() => setShowModifyBookingsDocumentation((prev) => !prev)}
                                            />
                                        }
                                        open={showModifyBookingsModal}
                                        onCancel={handleModifyBookingsModalClose}
                                        footer={[
                                            <Button key="cancel" onClick={handleModifyBookingsModalClose}>
                                                {"Abbrechen"}
                                            </Button>,
                                            <Button key="confirm" type="primary" onClick={handleModifyBookingsModalConfirm}>
                                                {isSuperUser && plannerMode ? "Im neuen Preismodell speichern" : "Bestätigen und fortfahren"}
                                            </Button>,
                                        ]}
                                        width="80%"
                                        style={{top: 60}}
                                        bodyStyle={{height: "75vh", paddingTop: 0}}
                                    >
                                        <ModifyBookingsDocumentation
                                            asModal={true}
                                            open={showModifyBookingsDocumentation}
                                            onClose={() => setShowModifyBookingsDocumentation(false)}
                                        />
                                        <ModalContainer
                                            courseId={course.id}
                                            formikProps={formikProps}
                                            bookingsOfCourse={bookingsOfCourse}
                                            plannerMode={plannerMode}
                                            onPlannerModeChange={setPlannerMode}
                                        />
                                    </Modal>
                                )}
                                {billedLessonsData && (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message={i18n.containers.courses.CourseEdit.billedLessons.generalWarning.title()}
                                        description={i18n.containers.courses.CourseEdit.billedLessons.generalWarning.description({
                                            count: String(billedCount),
                                        })}
                                        style={{
                                            marginBottom: 16,
                                            maxWidth: panelWidth,
                                            marginLeft: "auto",
                                            marginRight: "auto",
                                        }}
                                    />
                                )}
                                {renderSteps(formikProps)}
                                <LayoutForm
                                    onFinish={handleSubmit}
                                    style={{
                                        width: panelWidth,
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                    }}
                                    onKeyPress={handleKeyPress}
                                >
                                    {formikProps.values.allowLessonBooking && <LessonBookingBadge margin={[0, -15]} />}
                                    {renderStepContent(formikProps, status)}
                                    <FormButtonsStandard
                                        i18nResetFields={i18n.containers.courses.CourseCreate.contextButtons.resetFields()}
                                        i18nSubmit={i18n.containers.courses.CourseCreate.contextButtons.saveCourse()}
                                        onResetFields={resetForm}
                                        loading={graphqlLoading}
                                        style={{
                                            gridColumn: "1 / -1",
                                        }}
                                    />
                                </LayoutForm>
                            </>
                        );
                    }}
                </Formik>
            </PageContentWrapper>
        );
    };

    if (asModalContent) {
        return renderContent();
    }

    return <Page content={renderContent()} />;
};

// Observer-Komponente die Änderungen in formikProps.values.allowLessonBooking überwacht
const FormikObserver: FC<{
    formikProps: FormikProps<EditCourseInput>;
    onAllowLessonBookingChange?: (allowLessonBooking: boolean) => void;
}> = ({formikProps, onAllowLessonBookingChange}) => {
    const prevAllowLessonBookingRef = React.useRef<boolean | undefined>(undefined);

    // Wenn sich allowLessonBooking ändert, informiere den Parent
    React.useEffect(() => {
        const currentValue = Boolean(formikProps.values.allowLessonBooking);

        // Beim ersten Render: Speichere nur den Wert, rufe Callback nicht auf
        if (prevAllowLessonBookingRef.current === undefined) {
            prevAllowLessonBookingRef.current = currentValue;

            return;
        }

        // Wenn sich der Wert geändert hat, informiere den Parent (in BEIDE Richtungen)
        if (prevAllowLessonBookingRef.current !== currentValue) {
            onAllowLessonBookingChange?.(currentValue);
        }

        prevAllowLessonBookingRef.current = currentValue;
    }, [formikProps.values.allowLessonBooking, onAllowLessonBookingChange]);

    return null; // Diese Komponente rendert nichts
};

export const prepareUpdatedBookingsData = ({
    bookingsOfCourse,
    courseId,
}: {
    bookingsOfCourse?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"];
    courseId: Course["id"];
}): Array<EditBookingInputBookableLessons> | undefined => {
    const updatedBookingsData =
        bookingsOfCourse?.map((booking) => {
            return {
                id: booking.id,
                courseId,
                courseLessonIds: booking.courseLessonBookings?.map((courseLessonBooking) => courseLessonBooking.courseLessonId),
                paymentType: booking.paymentType,
                paymentMethodId: booking.paymentMethodId,
                // Buchungs-Rabatte mitsenden — sonst rechnet der Server bei Course-Updates
                // (z. B. Preis pro Lektion ändern, „Gebuchte Einheiten anpassen"-Modal)
                // die grossPrice ohne die bestehenden Abzüge neu und überschreibt sie auf 0.
                // Symmetrie zu Bugfix 04689d7 (discount): creditedBalance + memberDiscount
                // brauchen denselben Fix, sonst zeigt die Adjust-Preview einen zu hohen
                // Gesamtpreis (Discount wird respektiert, Gutschrift/MemberDiscount nicht).
                discount: booking.discount ?? null,
                discountName: booking.discountName ?? null,
                creditedBalance: booking.creditedBalance ?? null,
                memberDiscount: booking.memberDiscount ?? null,
                // sc-3028: omitMemberDiscount muss propagiert werden, sonst ergänzt
                // createPaymentPreview einen Mitgliederrabatt aus Course+Attendee, auch
                // wenn der Admin ihn beim Buchen explizit NICHT angewendet hat.
                // Ableitung aus booking.memberDiscount: ist der EUR-Wert null/0, war
                // der Mitgliederrabatt nicht aktiv → omit=true. Bei aktivem Rabatt
                // (memberDiscount > 0) lassen wir den Server frisch aus Course+Attendee
                // berechnen — falls dabei ein anderer Wert herauskommt (z. B. weil sich
                // der CoursePreis-Discount geändert hat), spiegelt das die aktuelle
                // Realität wider; das ist analog zum Bugfix 04689d7 (discount-Pfad).
                omitMemberDiscount: !booking.memberDiscount,
                // Explizit nur die im UpdateScheduledPaymentByBookingInput erlaubten Felder
                // mitgeben. Die Read-Query `GetBookingsByCourseIdWithCourseLessonBookings`
                // selektiert seit B-223 Wave 7 zusätzlich `manuallyAdjustedAt`,
                // `manuallyAdjustedByUserId`, `manuallyAdjustedBy`, `deletedAt` für die
                // Anzeige des Pin-Indikators in `OpenPaymentsList` — diese gehören NICHT
                // ins Mutation-Payload, sonst lehnt das Backend das Update mit
                // "Field X is not defined by type UpdateScheduledPaymentByBookingInput" ab.
                scheduledPayments: booking.scheduledPayments?.map((sp) => ({
                    id: sp.id,
                    paymentAmount: sp.paymentAmount,
                    refundedAmount: sp.refundedAmount,
                    paymentDue: sp.paymentDue,
                    paymentDone: sp.paymentDone,
                    type: sp.type,
                    title: sp.title,
                    openAmount: sp.openAmount,
                    paymentProvider: sp.paymentProvider,
                    mollieMethod: sp.mollieMethod,
                })),
            };
        }) ?? [];

    return updatedBookingsData;
};
export const prepareInitialValues = (
    initialCourseValues: Omit<NonNullable<GetCourseByIdQuery["courseById"]>, "prefixedCourseNumber">,
    latestPriceData: number,
    bookingsOfCourse?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"],
    // Formular-State über den GraphQL-Input hinaus (includedEntranceFee, paymentMonthly, updatedBookingsData, …)
): EditCourseInput & Record<string, unknown> => {
    return {
        ...initialCourseValues,
        courseTypeId: initialCourseValues.courseType.id,
        locationId: initialCourseValues.location.id,
        venueId: initialCourseValues.venue.id,
        vat: isDefined(initialCourseValues.coursePrice[latestPriceData] && initialCourseValues.coursePrice[latestPriceData].vat) ? initialCourseValues.coursePrice[latestPriceData].vat : undefined,
        fee: isDefined(initialCourseValues.coursePrice[latestPriceData] && initialCourseValues.coursePrice[latestPriceData].grossPrice)
            ? initialCourseValues.coursePrice[latestPriceData].grossPrice
            : undefined,
        discount: isDefined(initialCourseValues.coursePrice[latestPriceData]?.discount) ? initialCourseValues.coursePrice[latestPriceData].discount : undefined,
        discountUnit: isDefined(initialCourseValues.coursePrice[latestPriceData]?.discountUnit) ? (initialCourseValues.coursePrice[latestPriceData].discountUnit as DiscountUnit) : undefined,
        includedEntranceFee: isDefined(initialCourseValues.coursePrice[latestPriceData]?.includedEntranceFee) ? initialCourseValues.coursePrice[latestPriceData].includedEntranceFee : null,
        paymentInterval: isDefined(initialCourseValues.paymentInterval) ? initialCourseValues.paymentInterval : undefined,

        defaultLessonCount: initialCourseValues.lessons.length,
        lessons: initialCourseValues.lessons.map((lesson) => {
            return {
                id: lesson.id,
                locationId: lesson.locationId,
                venueId: lesson.venueId,
                instructors: Array.isArray(lesson.instructors)
                    ? lesson.instructors.map(({id}) => ({
                          id,
                      }))
                    : undefined,
                startDateTime: lesson.startDateTime,
                endDateTime: lesson.endDateTime,
            };
        }),
        paymentMonthly: initialCourseValues.allowMonthlyPayment ?? false,
        monthlySchedule: initialCourseValues.monthlySchedule ? initialCourseValues.monthlySchedule : MonthlySchedule.Consistent,
        CourseNumberFormat: initialCourseValues.courseNumberFormat,
        customCourseNumberPrefix: isDefined(initialCourseValues.customCourseNumberPrefix) ? initialCourseValues.customCourseNumberPrefix : "",
        courseId: isDefined(initialCourseValues.id) ? initialCourseValues.id : undefined,
        installmentRates: initialCourseValues.installmentRates,
        // Zusatzposten: id MUSS mitlaufen (ZP-01) — der Server macht daraus einen
        // Upsert; ohne id würde jeder Save die IDs rotieren und gebuchte
        // Referenzen (scheduledPayment.additionalItemId) verwaisen lassen.
        additionalItems: (initialCourseValues.additionalItems ?? []).map((item) => ({
            id: item.id,
            title: item.title,
            grossPrice: Number(item.grossPrice),
            mode: item.mode,
            vat: item.vat ?? null,
            description: item.description ?? null,
            // §4.2 Optimistic Concurrency: Lade-Stand der Zeile — der Server
            // meldet einen Konflikt, wenn sie zwischenzeitlich geändert wurde.
            updateDate: (item as {updateDate?: Date}).updateDate,
        })),
        paymentType: initialCourseValues.paymentType,
        // Anker der Monatsraten: am Kurs gespeichert, sonst erste Rate eines Monatskurses (#1459)
        dateOfFirstInstallment: initialCourseValues.dateOfFirstInstallment ?? (initialCourseValues.paymentType === PaymentType.Monthly ? initialCourseValues.installmentRates?.[0]?.installmentDue : undefined),
        allowLessonBooking: initialCourseValues.allowLessonBooking,
        maxAssistantsPerLesson: initialCourseValues.maxAssistantsPerLesson ?? null,
        customInvoiceDueDays: initialCourseValues.customInvoiceDueDays ?? null,
        ...getCourseLateEntryPricingInitialValues(initialCourseValues),
        updatedBookingsData: prepareUpdatedBookingsData({
            bookingsOfCourse,
            courseId: initialCourseValues.id,
        }),
        // scheduledPayments: initialCourseValues.scheduledPayments,
    };
};

export default CourseEditForm;
